import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useFetchSettings } from '../../hooks/useFetchSettings';

export interface SettingsSyncValue {
  loading: boolean;
  fetchSettings: (refreshApp?: boolean) => void;
}

/**
 * The old single-component Settings screen called `useFetchSettings()` once, so
 * one `loading` flag disabled every row while a sync was in flight. Now that the
 * screen is split into separately-mounted panels, each calling the hook would
 * create its own Apollo query with its own `loading` that never flips. This
 * context shares the shell's single instance instead.
 *
 * The default keeps panels renderable on their own (in tests) with the same
 * not-syncing state they would otherwise observe.
 */
const SettingsSyncContext = createContext<SettingsSyncValue>({
  loading: false,
  fetchSettings: () => undefined,
});

export const SettingsSyncProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { loading, fetchSettings } = useFetchSettings();

  // The shell re-renders on every nav-row tap (`selectedSectionId` changes), so
  // an inline object literal here would hand every consumer a fresh identity
  // each time and re-render them for no reason.
  const value = useMemo(
    () => ({ loading, fetchSettings }),
    [loading, fetchSettings],
  );

  return (
    <SettingsSyncContext.Provider value={value}>
      {children}
    </SettingsSyncContext.Provider>
  );
};

export const useSettingsSync = (): SettingsSyncValue =>
  useContext(SettingsSyncContext);

export default SettingsSyncContext;
